import {useEffect, useState} from 'react'
import {Form} from 'antd'
import http from '@/biz/http.mjs'
import {InputText} from '@/components/Form'
import {Confirm, Prompt, useOpenModal} from '@/components/Modal'
import runAsync from '@/script/runAsync.mjs'
import {setImmediate} from '@/script/timer.mjs'
import _useAi from '../useAi.mjs'

const POLL_INTERVAL = 2000

export const useAi = ({agentCode, businessKey, ...options}) => {
    const openModal = useOpenModal()
    const _ai = _useAi(options)
    const [agent, setAgent] = useState()

    const ai = {
        ..._ai,
        agentCode,
        scene: 'AGENT',
        _agent: agent,

        async chat(args) {
            const {agentInstanceId, pkid: sessionId} = this.getSession()

            return http.post(
                '/ai/chatWithAgentInstance',

                {
                    agentId: agent.agentId,
                    agentInstanceId,
                    sessionId,
                    llmId: this.template.llmId,
                    ...args
                },
            )
        },

        async clearContext() {
            const isConfirmed = await openModal(
                <Confirm>是否清空当前会话的上下文？</Confirm>
            )

            if (! isConfirmed) {
                return
            }

            const {agentInstanceId, pkid: sessionId} = this.getSession()

            await runAsync(
                () => http.post(
                    '/ai/clearAgentInstanceSessionContext',
                    {agentInstanceId, sessionId}
                ),

                {
                    action: '清空会话上下文',
                    success: {show: true},
                }
            )
        },

        async createSession() {
            const {agentInstanceId} = this.getSession()

            const agent = await runAsync(
                () => http.post(
                    '/ai/createAgentInstanceSession',
                    {agentInstanceId},
                ),

                {action: '创建新会话'}
            )

            this._completeMessages(agent)
            setAgent(agent)
            this._initMessages(agent)
        },

        async deleteSession(session) {
            const isConfirmed = await openModal(
                <Confirm>是否删除会话？</Confirm>
            )

            if (! isConfirmed) {
                return
            }

            const {agentInstanceId, pkid: sessionId} = session

            const agent = await runAsync(
                () => http.post(
                    '/ai/removeAgentInstanceSession',
                    {agentInstanceId, sessionId},
                ),

                {action: '删除会话'}
            )

            this._completeMessages(agent)
            setAgent(agent)
            this._initMessages(agent)
        },

        async editSession(session) {
            const data = await openModal(
                <Prompt title="重命名会话">
                    <Form.Item name="sessionName">
                        <InputText allowClear />
                    </Form.Item>
                </Prompt>
            )

            const {agentInstanceId, pkid: sessionId} = session

            const agent = await runAsync(
                () => http.post(
                    '/ai/renameAgentInstanceSession',
                    {agentInstanceId, sessionId, ...data},
                ),

                {action: '更新会话'}
            )

            this._completeMessages(agent)
            setAgent(agent)
        },

        getIsReady() {
            return (
                _ai.getIsReady() &&
                'READY' === agent?.status
            )
        },

        getIsRunning() {
            return (
                _ai.getIsRunning() ||
                'RUNNING' === agent?.status
            )
        },

        getSession() {
            return this.getSessions().at(-1)
        },

        getSessions() {
            return this._agent?.sessions ?? []
        },

        async run() {
            await _ai.run()
            const files = await this._uploadDraftFiles()
            const {agentInstanceId, pkid: sessionId} = this.getSession()

            const userMessage = {
                ...this.draft,
                files,
                role: 'user',
            }

            if (
                this.draft.content ||
                0 < files.length
            ) {
                this.addMessage(userMessage)
            }

            await http.post(
                '/ai/chatWithAgentInstance',

                {
                    agentId: agent.agentId,
                    agentInstanceId,
                    attachmentMap: {files: files.map(e => e.fileId)},
                    sessionId,
                    llmId: this.template.llmId,
                    userMessage: this.draft.content,
                    bizMap: this._getBizMap(),
                },

                {signal: this._getSignal()},
            )

            this._poll()
        },

        async setSession(session) {
            const agent = await runAsync(
                () => {
                    const {agentInstanceId, pkid: sessionId} = session

                    return http.post(
                        '/ai/switchAgentInstanceSession',
                        {agentInstanceId, sessionId}
                    )
                },

                {action: '切换会话'},
            )

            this._completeMessages(agent)
            setAgent(agent)
            this._initMessages(agent)
        },

        async stop() {
            const {agentInstanceId} = this.getSession()

            await runAsync(
                () => http.post(
                    '/ai/sendAgentInstanceInterruptSignal',
                    {agentInstanceId}
                ),

                {action: '终止智能体'}
            )

            this._poll()
        },

        _completeMessages(agent) {
            const sessionIdToMessages = new Map(
                this.getSessions().map(e => [e.pkid, e.messages])
            )

            for (const s of agent.sessions) {
                const messages = sessionIdToMessages.get(s.pkid)

                if (messages) {
                    s.messages = messages
                }
            }
        },

        _getBizMap() {
            const {pkid: prjId, prjName, prjNo, sVer} = this.project

            return {
                prjId,
                prjName,
                prjNo,
                sVer,
                ...this.bizData,
            }
        },

        async _init() {
            const agent = await runAsync(
                () => http.post(
                    '/ai/initAgentInstance',

                    {
                        agentCode: this.agentCode,
                        businessKey,
                        usageScenarioCode: 'AGENT',
                    }
                ),

                {action: '初始化智能体实例'}
            )

            setAgent(agent)
            this._agent = agent
            this._poll()
        },

        _initMessages(agent) {
            const messages = (agent.sessions?.at(-1)?.messages ?? []).map(
                raw => {
                    const {
                        messageContent,
                        messageContentType,
                        messageType,
                    } = raw

                    if ('user' === messageType) {
                        const {
                            attachmentMap,
                            message,
                        } = JSON.parse(messageContent)

                        return {
                            content: message,
                            files: Object.values(attachmentMap).flat(),
                            raw,
                            role: messageType,
                            type: messageContentType,
                        }
                    }
                    else if (/exception\/json/.test(messageContentType)) {
                        const {message, stacktrace} = JSON.parse(messageContent)

                        const content = ```
                        ${message}

                        \`\`\`txt
                        ${stacktrace}
                        \`\`\`
                        ```

                        return {
                            content,
                            raw,
                            role: 'error',
                            type: messageContentType,
                        }
                    }
                    else {
                        return {
                            content: messageContent,
                            raw,
                            role: messageType,
                            type: messageContentType,
                        }
                    }
                }
            )

            this.setMessages(messages)
        },

        _poll() {
            const {agentInstanceId, pkid: sessionId} = this.getSession()

            const poll = async stop => {
                const {session, ...agent} = await http.post(
                    '/ai/getAgentInstanceLatest',
                    {agentInstanceId, sessionId}
                )

                if ('RUNNING' !== agent.status) {
                    stop()
                    this.setIsRunning(false)
                }

                agent.sessions = this._agent.sessions.with(-1, session)
                setAgent(agent)
                this._initMessages(agent)
            }

            setImmediate(
                POLL_INTERVAL,
                poll,
                {signal: this._getSignal()}
            )
        },
    }

    useEffect(
        () => {
            ai._init()
        },

        []
    )

    return ai
}
